import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Printer, Upload, Loader2, Play, Volume2, CheckCircle2, AlertTriangle, Headphones, Camera, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Paper-Based Listening Flow:
 * 1) Brief
 * 2) Print Answer Sheet (browser print)
 * 3) Play full 4-part audio sequentially (one play, no pause)
 * 4) 10-minute transfer time
 * 5) Upload photo of the answer sheet → backend OCR (gpt-4o vision)
 * 6) Review/correct extracted answers
 * 7) Submit → graded result
 */
const TRANSFER_SECS = 10 * 60;

export default function IELTSPaperListening() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState("brief"); // brief | print | listening | transfer | upload | review | done
  const [mock, setMock] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [partIdx, setPartIdx] = useState(0);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [transferLeft, setTransferLeft] = useState(TRANSFER_SECS);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/ielts/listening-mock").then((r) => setMock(r.data));
    api.get("/ielts/listening-mock/answer-sheet").then((r) => setSheet(r.data));
  }, []);

  // Transfer timer
  useEffect(() => {
    if (stage !== "transfer") return;
    const t = setInterval(() => {
      setTransferLeft((s) => {
        if (s <= 1) { clearInterval(t); setStage("upload"); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [stage]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const playPart = async (idx) => {
    if (!mock) return;
    const part = mock.parts[idx];
    try {
      const r = await api.get(part.audio_url.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => {
        if (idx + 1 < mock.parts.length) {
          setPartIdx(idx + 1);
          setTimeout(() => playPart(idx + 1), 500);
        } else {
          setStage("transfer");
        }
      };
      try { await a.play(); }
      catch (e) { setAudioBlocked(true); }
    } catch (e) { toast.error("Audio failed to load"); }
  };

  const beginListening = () => {
    setStage("listening");
    setPartIdx(0);
    setTimeout(() => playPart(0), 600);
  };

  const onFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image (JPG/PNG)"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const runOCR = async () => {
    if (!imagePreview) { toast.error("Pick an image first"); return; }
    setOcrLoading(true);
    try {
      const r = await api.post("/ielts/listening-mock/ocr", { image_base64: imagePreview });
      setAnswers(r.data.answers || {});
      setStage("review");
      toast.success("Answers extracted — please review them carefully.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR failed");
    } finally {
      setOcrLoading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/ielts/listening-mock/submit", { answers, format: "paper" });
      setResult(r.data);
      setStage("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (!mock || !sheet) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  // ============================ BRIEF
  if (stage === "brief") {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 md:p-10 flex items-center justify-center" data-testid="paper-brief">
        <div className="max-w-2xl space-y-6">
          <div className="flex items-center gap-3">
            <Printer className="w-8 h-8 text-[#F77F00]" />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">IELTS · Paper-based simulation</div>
              <h1 className="font-display text-4xl">{mock.title}</h1>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-sm leading-relaxed space-y-2">
            <div className="font-semibold mb-1">How this works</div>
            <ol className="list-decimal pl-5 space-y-1.5 text-white/85">
              <li>Print the official-style answer sheet (next step).</li>
              <li>Get a pen — you&apos;ll write your answers on paper.</li>
              <li>Start the audio. It plays the four parts back-to-back <strong className="text-[#FB7185]">once only</strong>.</li>
              <li>After the audio, you get <strong className="text-[#FB7185]">10 minutes</strong> to transfer your answers neatly.</li>
              <li>Take a photo of your answer sheet and upload — our AI vision will read your handwriting.</li>
              <li>Review the extracted answers before submitting for grading.</li>
            </ol>
          </div>
          <button onClick={() => setStage("print")} data-testid="goto-print-btn"
            className="w-full px-7 py-4 rounded-2xl bg-[#F77F00] hover:bg-[#D26900] text-white font-bold text-lg inline-flex items-center justify-center gap-2">
            <Printer className="w-5 h-5" /> Continue — Print Answer Sheet
          </button>
          <button onClick={() => nav("/app/ielts/mock")} className="w-full text-sm text-white/50 hover:text-white">Cancel</button>
        </div>
      </div>
    );
  }

  // ============================ PRINT
  if (stage === "print") {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Toolbar (hidden when printing) */}
        <div className="print:hidden sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between" data-testid="print-toolbar">
          <div className="text-sm text-slate-600">Step 2 — Print your answer sheet, then start the audio.</div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} data-testid="print-btn"
              className="px-4 py-2 rounded-full bg-[#003049] text-white text-sm inline-flex items-center gap-2"><Printer className="w-4 h-4" /> Print</button>
            <button onClick={beginListening} data-testid="start-audio-btn"
              className="px-4 py-2 rounded-full bg-[#F77F00] text-white text-sm inline-flex items-center gap-2">
              <Play className="w-4 h-4" /> I&apos;ve printed — start audio
            </button>
          </div>
        </div>
        {/* Printable sheet */}
        <div className="max-w-3xl mx-auto p-8 bg-white print:p-0 print:max-w-none">
          <div className="text-center mb-6">
            <div className="text-xs uppercase tracking-widest text-slate-500">IELTS Listening · Answer Sheet</div>
            <div className="font-display text-2xl text-[#003049] mt-1">{sheet.title}</div>
            <div className="text-xs text-slate-500 mt-1">Candidate: ____________________________ &nbsp; Date: __________</div>
          </div>
          <ol className="text-xs text-slate-600 list-disc pl-5 mb-6 space-y-0.5">
            {sheet.instructions.map((ins, i) => <li key={i}>{ins}</li>)}
          </ol>
          {sheet.parts.map((p) => (
            <div key={p.id} className="mb-6 break-inside-avoid">
              <div className="font-display text-base text-[#003049] border-b border-slate-300 pb-1 mb-3">{p.title}</div>
              <div className="space-y-2">
                {p.rows.map((r) => (
                  <div key={r.n} className="grid grid-cols-[40px_1fr_220px] items-center gap-3 text-xs">
                    <div className="font-bold text-slate-900">{r.n}.</div>
                    <div className="text-slate-700">{r.prompt}</div>
                    {r.type === "mcq" ? (
                      <div className="flex gap-2">
                        {["A", "B", "C", "D"].slice(0, (r.choices || []).length).map((ltr) => (
                          <div key={ltr} className="w-7 h-7 border-2 border-slate-400 rounded-full flex items-center justify-center text-[10px] font-bold">{ltr}</div>
                        ))}
                      </div>
                    ) : (
                      <div className="border-b-2 border-slate-700 h-7"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============================ LISTENING / TRANSFER
  if (stage === "listening" || stage === "transfer") {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid={`paper-stage-${stage}`}>
        <div className="bg-slate-950 border-b border-white/10 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Headphones className="w-5 h-5 text-[#F77F00]" />
            <div className="text-sm">IELTS Listening · Paper-based · {stage === "listening" ? `Part ${partIdx + 1} of 4` : "Transfer time"}</div>
          </div>
          {stage === "transfer" && (
            <div className="font-mono text-2xl tabular-nums">{fmt(transferLeft)}</div>
          )}
        </div>
        {stage === "listening" ? (
          <div className="flex-1 flex items-center justify-center text-center p-10">
            <div className="space-y-6 max-w-xl">
              <div className="w-32 h-32 mx-auto rounded-full bg-[#F77F00]/20 border-2 border-[#F77F00] flex items-center justify-center">
                <Volume2 className="w-14 h-14 text-[#F77F00] animate-pulse" />
              </div>
              <div className="font-display text-3xl">{mock.parts[partIdx].title}</div>
              <div className="text-white/70">{mock.parts[partIdx].context}</div>
              <div className="text-sm text-white/50">
                The audio is playing — write your answers on the printed sheet. <br />
                The next part will begin automatically when this one ends.
              </div>
              {audioBlocked && (
                <button onClick={() => { audioRef.current?.play(); setAudioBlocked(false); }} className="px-5 py-2.5 rounded-full bg-[#F77F00] text-white">
                  Tap to enable audio
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-10">
            <div className="max-w-xl text-center space-y-5">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
              <div className="font-display text-3xl">Transfer time — 10 minutes</div>
              <div className="text-white/70 leading-relaxed">
                Carefully copy or finalise your answers on the printed sheet. <br />
                When you&apos;re ready (or when the timer reaches 00:00) you&apos;ll move to photo upload.
              </div>
              <button onClick={() => setStage("upload")} data-testid="goto-upload-btn"
                className="px-7 py-3 rounded-full bg-[#F77F00] text-white font-semibold">
                I&apos;m done — upload my sheet
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================ UPLOAD
  if (stage === "upload") {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="paper-upload">
        <div className="max-w-2xl mx-auto space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Step 5</div>
            <h1 className="font-display text-3xl text-[#003049] mt-1">Upload your answer sheet</h1>
            <p className="text-slate-600 mt-1 text-sm">Take a clear photo of the whole sheet. We use AI vision to extract your answers — you&apos;ll review them on the next screen.</p>
          </div>
          <label className="block border-2 border-dashed border-slate-300 hover:border-[#F77F00] rounded-3xl p-10 text-center cursor-pointer bg-white">
            <input data-testid="file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            {imagePreview ? (
              <div className="space-y-3">
                <img src={imagePreview} alt="" className="max-h-64 mx-auto rounded-xl border border-slate-200" />
                <div className="text-sm text-slate-500">Click to choose a different photo</div>
              </div>
            ) : (
              <div className="space-y-3">
                <Camera className="w-12 h-12 text-[#003049] mx-auto" />
                <div className="font-display text-lg text-[#003049]">Tap to take a photo or choose a file</div>
                <div className="text-xs text-slate-500">JPG / PNG · max 8MB · please keep the sheet flat and well-lit</div>
              </div>
            )}
          </label>
          <button onClick={runOCR} disabled={!imageFile || ocrLoading} data-testid="run-ocr-btn"
            className="w-full py-4 rounded-2xl bg-[#F77F00] hover:bg-[#D26900] disabled:opacity-50 text-white font-bold inline-flex items-center justify-center gap-2">
            {ocrLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Extracting answers…</> : <><Upload className="w-5 h-5" /> Extract answers with AI</>}
          </button>
        </div>
      </div>
    );
  }

  // ============================ REVIEW
  if (stage === "review") {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="paper-review">
        <div className="max-w-3xl mx-auto space-y-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Step 6</div>
            <h1 className="font-display text-3xl text-[#003049] mt-1">Review your extracted answers</h1>
            <p className="text-slate-600 text-sm mt-1">Our AI did its best — please correct any answer that looks wrong. Then submit for grading.</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 max-h-[60vh] overflow-y-auto">
            <div className="grid sm:grid-cols-2 gap-2">
              {mock.parts.flatMap((p) => p.questions).map((q) => (
                <div key={q.n} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5">
                  <div className="w-9 text-sm font-bold text-slate-500">{q.n}</div>
                  <input
                    data-testid={`review-${q.n}`}
                    value={answers[q.n] || ""}
                    onChange={(e) => setAnswers((a) => ({ ...a, [q.n]: e.target.value.toUpperCase() }))}
                    className="flex-1 border-b border-slate-300 focus:border-[#F77F00] outline-none text-sm py-1 uppercase tracking-wide"
                    placeholder="—"
                  />
                </div>
              ))}
            </div>
          </div>
          <button onClick={submit} disabled={submitting} data-testid="submit-paper-btn"
            className="w-full py-4 rounded-2xl bg-[#003049] hover:bg-[#0C4B66] text-white font-bold inline-flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Grading…</> : <>Submit for grading <ChevronRight className="w-5 h-5" /></>}
          </button>
        </div>
      </div>
    );
  }

  // ============================ DONE
  if (stage === "done" && result) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="paper-result">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-gradient-to-br from-[#003049] to-[#0C4B66] text-white rounded-3xl p-8">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">IELTS Listening · Paper-based</div>
            <div className="font-display text-7xl mt-2">Band {result.band}</div>
            <div className="text-sm text-white/75 mt-2">{result.correct}/{result.total} correct</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {result.section_breakdown.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.part}</div>
                <div className="font-display text-3xl text-[#003049] mt-1">{s.correct}/{s.total}</div>
              </div>
            ))}
          </div>
          <button onClick={() => nav("/app")} className="px-6 py-3 rounded-full bg-[#F77F00] text-white text-sm font-semibold">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return null;
}
